import { useLanguage, useLayout } from '@/hooks/Contexts'
import { useClickOutside } from '@/hooks/Index'
import { Action0, Action1 } from '@/utils/Types'
import moment from 'moment'
import { Dispatch, FC, Fragment, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import CaretIcon from './CaretIcon'
import LazyDiv from './LazyDiv'

type Selection = [undefined | null | number, undefined | null | string]

const MONTH_LIST = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']

const defaultSelection = (): Selection => {
  const now = moment.utc()
  return [now.year(), now.format('MMMM').toLowerCase()]
}

export const formatAsQry = (value: Selection) => {
  const monNum = MONTH_LIST.indexOf(value[1]) + 1
  return `${value[0]}-${monNum > 9 ? monNum : '0' + monNum}`
}

export const useMonthPicker = (resetOtherParams: Action0) => {
  const [filter, setFilter] = useState<Selection>(null) // selected
  const onApply = (selection: Selection) => {
    if (selection?.toString() != filter?.toString()) {
      resetOtherParams()
      setFilter(selection) // setSelected
    }
  }
  const onReset = async () => {
    await new Promise<void>(resolve => {
      resetOtherParams()
      setFilter(null)
      return resolve()
    })
  }
  return { filter, setFilter, onApply, onReset }
}

const MonthPicker: FC<{
  valueCtrl: [Selection, Dispatch<SetStateAction<Selection>>]
  onApply: Action1<Selection>
  className?: string
}> = ({ valueCtrl, onApply, className }) => {
  const { isDesktop } = useLayout()
  const {
    actions: { cancel },
    cart: { apply },
    months_abbr,
  } = useLanguage()

  const [show, setShow] = useState<boolean>(false)
  const wrapperRef = useRef(null)
  useClickOutside(wrapperRef, () => setShow(false))

  const [currYear, currMonth] = useMemo(() => {
    const tmp = defaultSelection()
    return [tmp[0], MONTH_LIST.indexOf(tmp[1])]
  }, [])
  const [selection, setSelection] = useState<Selection>(null)
  // eslint-disable-next-line no-unused-vars
  const [selected, setSelected] = valueCtrl
  useEffect(() => setSelection(selected), [selected])

  const onSelect = (option: number | string) => {
    let newVal: Selection = selection ? [...selection] : null
    if (typeof option == 'string' && MONTH_LIST.includes(option)) {
      newVal = [newVal?.[0], option]
    } else {
      newVal = [Number(option), null]
    }
    // when the current year is selected, month selection cannot surpass the current month
    if (newVal[0] == currYear && MONTH_LIST.indexOf(newVal[1]) > currMonth) {
      newVal[1] = MONTH_LIST[currMonth]
    }
    setSelection(newVal)
  }

  return (
    <Fragment>
      {show ? <div className='fixed inset-0 z-[8] h-screen w-screen bg-transparent ease-in-out'></div> : null}
      <div
        ref={wrapperRef}
        className={`relative z-[9] overflow-visible rounded-md border border-stone-300 bg-white/100 shadow-lg shadow-stone-200 ${className || ''}`}
      >
        <div onClick={() => setShow(prev => !prev)} className='flex w-full cursor-pointer items-center px-4 py-1'>
          <span className='hidden sm:inline'>Select date:&nbsp;</span>
          <span className='font-semibold capitalize'>{selected ? `${isDesktop ? selected[1] : months_abbr[selected[1]]} ${selected[0]}` : 'All'}</span>
          <CaretIcon contentOpen={show} className='ml-2 h-4 w-4' />
        </div>
        <div
          className={`absolute right-0 top-[calc(100%+12px)] grid min-w-max grid-cols-6 overflow-clip rounded-md bg-white/100 shadow-lg shadow-stone-200 transition${
            selected ? ' md:min-w-full' : ''
          } ${show ? 'animate-[fade-in-down_150ms_ease-out]' : 'pointer-events-none animate-[fade-out-up_150ms_ease-out_forwards]'}`}
        >
          {['all', currYear, currYear - 1, currYear - 2, currYear - 3].map((opt, idx) => (
            <button
              key={'month-picker-opt-' + opt}
              type='button'
              onClick={() => (opt == 'all' ? setSelection(null) : onSelect(opt))}
              className={`border-b px-4 py-2 capitalize ${idx < 2 ? 'col-span-3 odd:border-r' : 'col-span-2 even:border-x'} ${
                (selection ? selection[0] == opt : opt == 'all') ? 'bg-amber-v1' : 'bg-white/100 enabled:hover:bg-amber-v1 disabled:bg-neutral-100'
              }`}
            >
              {opt}
            </button>
          ))}
          <LazyDiv isActive={!!selection?.[0]} className='contents'>
            {MONTH_LIST.map((opt, idx) => (
              <button
                key={'month-picker-opt-' + opt}
                type='button'
                disabled={selection?.[0] == currYear && idx > currMonth}
                onClick={() => onSelect(opt)}
                className={`col-span-3 animate-fade-in-down border-b px-4 py-2 capitalize odd:border-r ${
                  selection?.includes(opt) ? 'bg-amber-v1' : 'bg-white/100 enabled:hover:bg-amber-v1 disabled:bg-neutral-100'
                }`}
              >
                {opt}
              </button>
            ))}
          </LazyDiv>
          <div className='col-span-full flex items-center justify-evenly gap-x-2 px-2 py-2 md:gap-x-4 md:px-4'>
            <button
              type='button'
              onClick={() => {
                setShow(false)
                setTimeout(() => setSelection(selected), 300)
              }}
              className='btn-rd-outline btn-hover-amber h-8 w-full'
            >
              {cancel}
            </button>
            <button
              type='button'
              disabled={selection && !selection.every(val => !!val)}
              onClick={() => {
                onApply(selection) // setSelected -> setSelection
                setShow(false)
              }}
              className='btn-rd-amber btn-hover-amber btn-disabled-grey h-8 w-full'
            >
              {apply}
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  )
}
export default MonthPicker
